import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard,
  ClipboardList,
  ChevronDown,
  Trash2,
  ArrowLeft,
  LogOut,
  Bell,
  MessageCircle,
  ExternalLink,
  Send,
  Wifi,
  WifiOff,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import AdminLogin from "@/components/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-sky-100 text-sky-800 border-sky-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotifs, setShowNotifs] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [waStatus, setWaStatus] = useState({ status: "loading", mode: "unknown" });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(res => { setUser(res.data); setChecking(false); })
      .catch(() => { setUser(null); setChecking(false); });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [bookingsRes, statsRes, notifsRes, waRes] = await Promise.all([
        axios.get(`${API}/bookings`, { withCredentials: true }),
        axios.get(`${API}/bookings/stats`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/whatsapp/status`, { withCredentials: true }).catch(() => ({ data: { status: "error", mode: "fallback_links" } })),
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
      setNotifications(notifsRes.data);
      setWaStatus(waRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/bookings/${id}/status`, { status }, { withCredentials: true });
      const d = res.data;
      if (d.wa_sent) {
        toast.success(`Status updated. WhatsApp sent automatically via API.`);
      } else {
        toast.success(`Status updated to ${status}`);
        if (d.whatsapp_notification_link) {
          toast(
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-green-600" />
              <a href={d.whatsapp_notification_link} target="_blank" rel="noopener noreferrer" className="text-green-700 underline text-sm">
                Click to notify customer on WhatsApp
              </a>
            </div>
          );
        }
      }
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const sendReminder = async (id) => {
    try {
      const res = await axios.post(`${API}/bookings/${id}/remind`, {}, { withCredentials: true });
      const d = res.data;
      if (d.wa_sent) {
        toast.success("Reminder sent automatically via WhatsApp API!");
      } else if (d.whatsapp_link) {
        toast(
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-green-600" />
            <a href={d.whatsapp_link} target="_blank" rel="noopener noreferrer" className="text-green-700 underline text-sm">
              Click to send reminder via WhatsApp
            </a>
          </div>
        );
      }
      fetchData();
    } catch {
      toast.error("Failed to send reminder");
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      toast.success("Booking deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete booking");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={setUser} />;
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-surface flex">
      <aside
        data-testid="admin-sidebar"
        className="w-64 bg-navy text-white hidden md:flex flex-col"
      >
        <div className="p-5 border-b border-white/10">
          <div className="bg-white rounded-lg p-3 inline-block">
            <img
              src="https://customer-assets.emergentagent.com/job_tankbuddy-gurgaon/artifacts/he99wde0_Gemini_Generated_Image_kjnrs1kjnrs1kjnr.png"
              alt="TankBuddy"
              className="h-8 w-auto"
            />
          </div>
        </div>
        <nav className="p-4 space-y-1">
          <button
            onClick={() => setActiveTab("dashboard")}
            data-testid="sidebar-dashboard-btn"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "dashboard" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}
          >
            <LayoutDashboard className="w-4 h-4" />
            <span className="text-sm font-medium">Dashboard</span>
          </button>
          <button
            onClick={() => setActiveTab("notifications")}
            data-testid="sidebar-notifications-btn"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "notifications" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}
          >
            <Bell className="w-4 h-4" />
            <span className="text-sm font-medium">Notifications</span>
            {unreadCount > 0 && (
              <span className="ml-auto bg-sky text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        </nav>
        <div className="mt-auto p-4 space-y-1">
          <Button
            asChild
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5"
          >
            <a href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Website
            </a>
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_tankbuddy-gurgaon/artifacts/he99wde0_Gemini_Generated_Image_kjnrs1kjnrs1kjnr.png"
                  alt="TankBuddy"
                  className="h-8 w-auto md:hidden"
                />
                <h1 className="font-heading font-bold text-2xl text-navy">
                  {activeTab === "dashboard" ? "Dashboard" : "Notifications"}
                </h1>
              </div>
              <p className="text-slate-500 text-sm">
                Welcome, {user.name} &middot; {user.email}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {waStatus.status === "configured" ? (
                  <span data-testid="wa-status-configured" className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                    <Wifi className="w-3 h-3" /> WhatsApp API Active
                  </span>
                ) : (
                  <span data-testid="wa-status-fallback" className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                    <WifiOff className="w-3 h-3" /> WhatsApp: Manual Links
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNotifs(!showNotifs)}
                className="relative md:hidden"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-sky text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <Button
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
                variant="outline"
                size="sm"
                className="md:hidden"
              >
                <LogOut className="w-4 h-4" />
              </Button>
              <Button
                asChild
                className="md:hidden bg-navy hover:bg-navy-light text-white rounded-full"
                size="sm"
              >
                <a href="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </a>
              </Button>
            </div>
          </div>

          {activeTab === "dashboard" && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Total", value: stats.total, color: "bg-navy" },
                  { label: "Pending", value: stats.pending, color: "bg-amber-500" },
                  { label: "Confirmed", value: stats.confirmed, color: "bg-sky" },
                  { label: "Completed", value: stats.completed, color: "bg-emerald-500" },
                ].map((s) => (
                  <div
                    key={s.label}
                    data-testid={`stat-${s.label.toLowerCase()}`}
                    className="bg-white border border-slate-200 rounded-xl p-5"
                  >
                    <p className="text-slate-500 text-xs font-medium mb-1">{s.label}</p>
                    <p className="font-heading font-bold text-2xl text-navy">{s.value}</p>
                    <div className={`w-8 h-1 rounded-full ${s.color} mt-2`} />
                  </div>
                ))}
              </div>

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h2 className="font-heading font-semibold text-lg text-navy">
                    All Bookings
                  </h2>
                </div>

                {loading ? (
                  <div className="p-12 text-center text-slate-500">Loading...</div>
                ) : bookings.length === 0 ? (
                  <div data-testid="no-bookings" className="p-12 text-center text-slate-500">
                    No bookings yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="bookings-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Preferred Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((b) => (
                          <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                            <TableCell className="text-sm text-slate-600">
                              {new Date(b.created_at).toLocaleDateString("en-IN")}
                            </TableCell>
                            <TableCell className="font-medium text-navy">{b.name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{b.phone}</TableCell>
                            <TableCell className="text-sm text-slate-600">{b.service}</TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {b.date || "—"}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`capitalize text-xs ${statusColors[b.status] || ""}`}
                              >
                                {b.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`remind-btn-${b.id}`}
                                  onClick={() => sendReminder(b.id)}
                                  title="Send reminder"
                                  className="text-sky hover:text-navy hover:bg-sky/10"
                                >
                                  <Clock className="w-3.5 h-3.5" />
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      data-testid={`status-dropdown-${b.id}`}
                                      className="text-xs"
                                    >
                                      Status
                                      <ChevronDown className="w-3 h-3 ml-1" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    {["pending", "confirmed", "completed", "cancelled"].map((s) => (
                                      <DropdownMenuItem
                                        key={s}
                                        onClick={() => updateStatus(b.id, s)}
                                        className="capitalize text-sm"
                                      >
                                        {s}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`delete-btn-${b.id}`}
                                  onClick={() => deleteBooking(b.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === "notifications" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-heading font-semibold text-lg text-navy">
                  Notifications
                </h2>
                <span className="text-xs text-slate-400">{notifications.length} total</span>
              </div>
              {notifications.length === 0 ? (
                <div className="p-12 text-center text-slate-500">No notifications yet</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {notifications.map((n, i) => (
                    <div key={i} className={`px-6 py-4 ${!n.read ? 'bg-sky/5' : ''}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm text-navy font-medium">{n.message}</p>
                            {n.wa_delivery === "api" && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                                <Wifi className="w-2.5 h-2.5" /> AUTO
                              </span>
                            )}
                            {n.wa_delivery === "fallback" && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                                MANUAL
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(n.created_at).toLocaleString("en-IN")}
                          </p>
                        </div>
                        {n.whatsapp_link && (
                          <a
                            href={n.whatsapp_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`notif-whatsapp-${i}`}
                            className="flex-shrink-0 inline-flex items-center gap-1 bg-whatsapp/10 text-whatsapp text-xs font-medium px-3 py-1.5 rounded-full hover:bg-whatsapp/20 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
